import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  type ReactNode,
} from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { Session, User } from '@supabase/supabase-js';
import { requestSignup } from '@upcaret/ui/auth';

type AuthContextValue = {
  session: Session | null;
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: unknown;
  refreshSession: () => Promise<Session | null>;
  signIn: (email: string, password: string) => Promise<void>;
  signUp: (
    email: string,
    password: string,
    redirectTo?: string,
  ) => Promise<void>;
  signOut: () => Promise<void>;
};

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

const SESSION_QUERY_KEY = ['auth', 'session'];

export function AuthProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  const isBrowser = typeof window !== 'undefined';

  const sessionQuery = useQuery({
    queryKey: SESSION_QUERY_KEY,
    queryFn: async (): Promise<Session | null> => {
      if (!isBrowser) return null;
      const { supabase } = await import('@upcaret/supabase-types/client');
      const { data, error } = await supabase.auth.getSession();
      if (error) throw error;
      return data.session ?? null;
    },
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    enabled: isBrowser,
  });

  const signInMutation = useMutation({
    mutationFn: async ({
      email,
      password,
    }: {
      email: string;
      password: string;
    }) => {
      const { supabase } = await import('@upcaret/supabase-types/client');
      const { error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: SESSION_QUERY_KEY });
    },
  });

  const signUpMutation = useMutation({
    mutationFn: async ({
      email,
      password,
      redirectTo,
    }: {
      email: string;
      password: string;
      redirectTo?: string;
    }) => {
      await requestSignup({ email, password, redirectTo });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: SESSION_QUERY_KEY });
    },
  });

  const signOutMutation = useMutation({
    mutationFn: async () => {
      const { supabase, signOutSafely } = await import('@upcaret/supabase-types/client');
      await signOutSafely(supabase);
    },
    onSuccess: () => {
      queryClient.setQueryData(SESSION_QUERY_KEY, null);
      queryClient.clear();
    },
  });

  useEffect(() => {
    if (!isBrowser) return;

    let unsub: (() => void) | undefined;

    (async () => {
      const { supabase } = await import('@upcaret/supabase-types/client');
      const { data } = supabase.auth.onAuthStateChange((_event, nextSession) => {
        queryClient.setQueryData(SESSION_QUERY_KEY, nextSession ?? null);
      });
      unsub = () => data.subscription.unsubscribe();
    })().catch((err) => {
      console.error('Auth subscription setup failed', err);
    });

    return () => {
      unsub?.();
    };
  }, [queryClient, isBrowser]);

  const refreshSession = useCallback(async () => {
    if (!isBrowser) return null;
    const { supabase } = await import('@upcaret/supabase-types/client');
    const { data, error } = await supabase.auth.getSession();
    if (error) throw error;
    queryClient.setQueryData(SESSION_QUERY_KEY, data.session ?? null);
    return data.session ?? null;
  }, [queryClient, isBrowser]);

  const value = useMemo<AuthContextValue>(() => {
    const session = sessionQuery.data ?? null;
    const user = session?.user ?? null;
    return {
      session,
      user,
      isAuthenticated: !!session,
      isLoading: sessionQuery.isLoading,
      error: sessionQuery.error,
      refreshSession,
      signIn: async (email, password) =>
        signInMutation.mutateAsync({ email, password }),
      signUp: async (email, password, redirectTo) =>
        signUpMutation.mutateAsync({ email, password, redirectTo }),
      signOut: async () => signOutMutation.mutateAsync(),
    };
  }, [
    sessionQuery.data,
    sessionQuery.isLoading,
    sessionQuery.error,
    refreshSession,
    signInMutation,
    signUpMutation,
    signOutMutation,
  ]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return ctx;
}
